import {
  useHttpClient,
  useNetworkStatus,
  useReducerStorage as useReducer,
  useToast,
} from '@airship-platform/airship-patterns';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { FinancingConfig } from './financingConfig.models';
import {
  financingConfigReducer,
  FinancingConfigState,
  initState,
} from './financingConfig.reducer';

interface FinancingConfigContextProps {
  financingConfigs: FinancingConfig[];
  isLoading: boolean;
  isLoaded: boolean;
  fetchFinancingConfigs: () => Promise<void>;
}

const FinancingConfigContext = createContext<
  FinancingConfigContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<FinancingConfigState>;
}>;

export const FinancingConfigProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    financingConfigReducer,
    {
      ...initState,
      ...initialState,
    },
    {
      key: 'airship-cache:financing-config',
      select: (s) => ({ ids: s.ids, entities: s.entities }),
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const financingConfigs = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const fetchFinancingConfigs: FinancingConfigContextProps['fetchFinancingConfigs'] =
    useCallback(async () => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_FINANCE_CONFIGS' });
      try {
        const financingConfigs = await httpClient.get<FinancingConfig[]>(
          `/organization-accounting/financing`
        );
        dispatch({
          type: 'FETCH_FINANCE_CONFIGS_SUCCESS',
          payload: { financingConfigs },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_FINANCE_CONFIGS_FAILURE',
          payload: { error: `Failed to fetch financingConfigs: ${error}` },
        });
      }
    }, [httpClient, isOnline]);

  return (
    <FinancingConfigContext.Provider
      value={{
        financingConfigs,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        fetchFinancingConfigs,
      }}
    >
      {children}
    </FinancingConfigContext.Provider>
  );
};

export const useFinancingConfig = (): FinancingConfigContextProps => {
  const context = useContext(FinancingConfigContext);
  if (context === undefined) {
    throw new Error(
      'useFinancingConfig must be used within an FinancingConfigProvider'
    );
  }
  return context;
};
